import React from 'react';
import {Link} from 'react-router';
import sessionStore from 'client/Session/SessionStore';
import MarketingBanner from 'client/components/MarketingBanner/MarketingBanner.react';
import {hasSubjectAccess} from 'lib/UserAccessUtil';
import {Banner} from '@albert-io/atomic';
import type {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

interface SubscriptionPrice {
  get(key: 'price' | 'expiresInMonths'): number;
}

interface SubscriptionPriceList {
  find(predicate: (price: SubscriptionPrice) => boolean): SubscriptionPrice | undefined;
  first(): SubscriptionPrice | undefined;
}

interface StudyGuideSubject {
  getId(): string;
  getName(): string;
  getBanner(): string | null;
  getUrlSlug(): string;
  getSubscriptionsPrices(): SubscriptionPriceList | null | undefined;
}

interface Props {
  subject: StudyGuideSubject;
  user?: UserModelV2 | null;
  className?: string;
}

export default class SubjectBanner extends React.Component<Props> {
  /**
   * The retail price shown to students: the 12-month subscription price when one
   * exists, otherwise any available price regardless of length. Copy stays
   * timeframe-free so the length of the fallback price doesn't matter.
   */
  getRetailPrice = (subject: StudyGuideSubject) => {
    const subscriptionPrices = subject.getSubscriptionsPrices();
    const selectedPrice =
      subscriptionPrices?.find((p) => p.get('expiresInMonths') === 12) ||
      subscriptionPrices?.first();
    return selectedPrice ? selectedPrice.get('price') / 100 : null;
  };

  /**
   * Resolves which banner (if any) a viewer should see on a subject guide:
   *  - A custom Dennis banner always wins.
   *  - On AP®/ACT®/SAT® subjects, an unlicensed viewer sees the marketing banner —
   *    the teacher copy for teachers/admins, the retail copy for guests and
   *    students who aren't in a classroom.
   *  - Everyone else (licensed viewers, students in a classroom, non test-prep
   *    subjects) sees no banner.
   *
   * @param subject
   * @param user
   * @param userHasSubjectAccess result of `hasSubjectAccess` — true when the viewer
   *   is licensed via individual purchase or a school license.
   */
  getBanner = (
    subject: StudyGuideSubject,
    user: UserModelV2 | null | undefined,
    userHasSubjectAccess: boolean
  ) => {
    const {className} = this.props;

    if (subject.getBanner()) {
      return (
        <Banner className={className}>
          <MarkdownRendererV2 text={subject.getBanner()} />
        </Banner>
      );
    }

    const name = subject.getName();
    const isTestPrepSubject =
      name.includes('AP®') || name.includes('ACT®') || name.includes('SAT®');

    if (!isTestPrepSubject || userHasSubjectAccess) {
      return null;
    }

    const isGuest = !sessionStore.hasValidSession();
    const isStudent = sessionStore.isStudent();

    // Students who belong to a classroom never see the marketing banner.
    if (isStudent && !user!.getStudent().getStudentClassrooms().isEmpty()) {
      return null;
    }

    // Logged-in non-students (teachers/admins) get the school-license copy.
    if (!isGuest && !isStudent) {
      return (
        <MarketingBanner
          className={className}
          text='Looking to help your students ace the exam?'
          headingText='Unlock all premium adaptive features + all test prep & core question banks with a school or district license.'
          buttonText='Explore Pricing'
          buttonProps={{
            as: 'a',
            href: '/pricing-schools'
          }}
        />
      );
    }

    // Guests and retail students (not in a classroom) get the retail upgrade copy.
    const price = this.getRetailPrice(subject);
    const marketplaceLink = `/marketplace/subjects?subject=${subject.getUrlSlug()}&subjectId=${subject.getId()}`;
    return (
      <MarketingBanner
        className={className}
        text='Looking to get a perfect score on the exam?'
        headingText={`Purchase this course for $${price} and get access to hundreds of authentic exam questions with detailed explanations.`}
        buttonText='Upgrade now'
        buttonProps={{
          as: Link,
          to: marketplaceLink
        }}
      />
    );
  };

  render() {
    const {subject, user} = this.props;
    const {isReady, value} = hasSubjectAccess(subject.getId());

    if (!isReady) {
      return null;
    }

    return this.getBanner(subject, user, value);
  }
}
